<script setup lang="ts">
import { ref, defineProps, defineEmits, reactive, watch } from 'vue';
import { Recipient, RecipientType } from '../types';
import { ChevronDownIcon, ChevronUpIcon, CalendarIcon, CheckIcon } from './Icons.js';
import { formatPhoneNumber } from './InvoiceComponents.vue';
import { createEmptyRecipient } from '../helpers/recipient'

const props = defineProps<{
  recipient: Recipient;
}>();

const emit = defineEmits<{
  (e: 'update:recipient', r: Recipient): void;
}>();

const isExpanded = ref(false);

const localRecipient = reactive(
	props.recipient
		? JSON.parse(JSON.stringify(props.recipient))
		: createEmptyRecipient()
)

const updatePerson = (field: string, value: any) => {
	if (!localRecipient.person) localRecipient.person = {}
	localRecipient.person[field] = value
	emit('update:recipient', JSON.parse(JSON.stringify(localRecipient)))
}

const updateLegal = (field: string, value: any) => {
	if (!localRecipient.legal) localRecipient.legal = {}
	localRecipient.legal[field] = value
	emit('update:recipient', JSON.parse(JSON.stringify(localRecipient)))
}

const changeType = (type: RecipientType) => {
	localRecipient.type = type

	if (type === RecipientType.INDIVIDUAL && !localRecipient.person) {
		localRecipient.person = {
			firstName: '',
			lastName: '',
			middleName: '',
			phone: '',
			email: '',
			RNOKPP: '',
			passport: '',
			birthDate: '',
			address: '',
			city: '',
			country: '',
			postCode: '',
			state: '',
		}
	}

	if (type === RecipientType.ENTITY && !localRecipient.legal) {
		localRecipient.legal = {
			name: '',
			EDRPOU: '',
			IBAN: '',
			actualCountry: '',
			country: '',
			city: '',
			street: '',
			building: '',
			vat: false,
			MFO: '',
		}
	}

	emit('update:recipient', JSON.parse(JSON.stringify(localRecipient)))

}

// Utils for formatting
const formatTaxId = (value: string) => value.replace(/\D/g, '').slice(0, 10);
const formatPostalCode = (value: string) => value.replace(/\D/g, '').slice(0, 5);
const formatIban = (value: string) => value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 29);
const formatPhone = (val: string) => {
    let digits = val.replace(/\D/g, '');
    if (digits.startsWith('0')) digits = '38' + digits;
    if (digits.startsWith('80')) digits = '3' + digits;
    digits = digits.slice(0, 12);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)} (${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)} (${digits.slice(3, 5)}) ${digits.slice(5)}`;
    if (digits.length <= 10) return `${digits.slice(0, 3)} (${digits.slice(3, 5)}) ${digits.slice(5, 8)}-${digits.slice(8)}`;
    return `${digits.slice(0, 3)} (${digits.slice(3, 5)}) ${digits.slice(5, 8)}-${digits.slice(8, 10)}-${digits.slice(10)}`;
};

watch(
	() => props.recipient,
	(val) => {
		Object.assign(localRecipient, JSON.parse(JSON.stringify(val)))
	},
	{ deep: true }
)
</script>

<template>
  <div class="border rounded-lg p-6 bg-white shadow-sm space-y-6">
    <h2 class="text-lg font-bold uppercase tracking-wide">{{ $t('invoices.recipient') }}</h2>

    <div class="flex flex-wrap items-center gap-6">
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="recipientType"
          class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
          :checked="localRecipient.type === RecipientType.INDIVIDUAL"
          @change="changeType(RecipientType.INDIVIDUAL)"
        />
        <span class="text-sm font-medium">{{ $t('invoices.individual') }}</span>
      </label>
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="recipientType"
          class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
          :checked="localRecipient.type === RecipientType.ENTITY"
          @change="changeType(RecipientType.ENTITY)"
        />
        <span class="text-sm font-medium">{{ $t('invoices.entity') }}</span>
      </label>
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="recipientType"
          class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
          :checked="localRecipient.type === RecipientType.ON_PAYMENT"
          @change="changeType(RecipientType.ON_PAYMENT)"
        />
        <span class="text-sm font-medium text-slate-500">{{ $t('invoices.on_payment') }}</span>
      </label>
    </div>

    <div class="min-h-[160px]">
      <!-- Individual Form -->
      <div v-if="localRecipient.type === RecipientType.INDIVIDUAL && localRecipient.person" class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 items-start">
        <div class="space-y-4">
           <!-- Reordered: Surname, Name, Patronymic -->
          <div class="grid grid-cols-[130px_1fr] items-center gap-4">
             <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.surname') }}<span class="text-red-500 ml-0.5">*</span></label></div>
             <input :value="localRecipient.person.lastName" @input="updatePerson('lastName', ($event.target as HTMLInputElement).value)" class="input-base" :placeholder="$t('invoices.placeholders.surname')" required />
          </div>

          <div class="grid grid-cols-[130px_1fr] items-center gap-4">
             <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.name') }}<span class="text-red-500 ml-0.5">*</span></label></div>
             <input :value="localRecipient.person.firstName" @input="updatePerson('firstName', ($event.target as HTMLInputElement).value)" class="input-base" :placeholder="$t('invoices.placeholders.name')" required />
          </div>

          <div class="grid grid-cols-[130px_1fr] items-center gap-4">
             <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.patronymic') }}</label></div>
             <input :value="localRecipient.person.middleName" @input="updatePerson('middleName', ($event.target as HTMLInputElement).value)" class="input-base" />
          </div>

          <!-- Expanded Fields Left Col -->
          <template v-if="isExpanded">
             <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.phone') }}</label><div class="text-[10px] text-slate-400 leading-tight mt-0.5">{{ $t('invoices.placeholders.or_email') }}</div></div>
               <div class="relative">
                 <input :value="localRecipient.person.phone" @input="updatePerson('phone', formatPhone(($event.target as HTMLInputElement).value))" class="input-base pl-7" :placeholder="$t('invoices.placeholders.email')" />
               </div>
             </div>

             <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.email') }}</label><div class="text-[10px] text-slate-400 leading-tight mt-0.5">{{ $t('invoices.placeholders.or_phone') }}</div></div>
                <input type="email" :value="localRecipient.person.email" @input="updatePerson('email', ($event.target as HTMLInputElement).value)" class="input-base" :placeholder="$t('invoices.placeholders.email')" />
             </div>

             <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.tax_id_individual') }}</label></div>
                <input :value="localRecipient.person.RNOKPP" @input="updatePerson('RNOKPP', formatTaxId(($event.target as HTMLInputElement).value))" class="input-base" maxlength="10" :placeholder="$t('invoices.placeholders.tax_id_10')" />
             </div>

             <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.passport') }}</label></div>
                <input :value="localRecipient.person.passport" @input="updatePerson('passport', ($event.target as HTMLInputElement).value)" class="input-base" />
             </div>

             <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.birth_date') }}</label></div>
                <div class="relative">
                   <input type="date" :value="localRecipient.person.birthDate" @input="updatePerson('birthDate', ($event.target as HTMLInputElement).value)" class="input-base" />
                </div>
             </div>
          </template>
        </div>

        <div class="space-y-4 h-full flex flex-col">
            <template v-if="!isExpanded">
               <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                 <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.phone') }}</label><div class="text-[10px] text-slate-400 leading-tight mt-0.5">{{ $t('invoices.placeholders.or_email_1') }}</div></div>
                 <div class="relative">
                   <input :value="localRecipient.person.phone" @input="updatePerson('phone', formatPhone(($event.target as HTMLInputElement).value))" class="input-base pl-7" :placeholder="$t('invoices.placeholders.phone')"/>
                 </div>
               </div>

               <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                  <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.email') }}</label><div class="text-[10px] text-slate-400 leading-tight mt-0.5">{{ $t('invoices.placeholders.or_phone_1') }}</div></div>
                  <input type="email" :value="localRecipient.person.email" @input="updatePerson('email', ($event.target as HTMLInputElement).value)" class="input-base" :placeholder="$t('invoices.placeholders.email')" />
               </div>
            </template>

            <!-- Expanded Right Col Address -->
            <template v-if="isExpanded">
                <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                   <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.country') }}</label></div>
                   <input :value="localRecipient.person.country" @input="updatePerson('country', ($event.target as HTMLInputElement).value)" class="input-base" />
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                   <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.region') }}</label></div>
                   <input :value="localRecipient.person.state" @input="updatePerson('state', ($event.target as HTMLInputElement).value)" class="input-base" />
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                   <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.city') }}</label></div>
                   <input :value="localRecipient.person.city" @input="updatePerson('city', ($event.target as HTMLInputElement).value)" class="input-base" />
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                   <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.address') }}</label></div>
                   <input :value="localRecipient.person.address" @input="updatePerson('address', ($event.target as HTMLInputElement).value)" class="input-base" :placeholder="$t('invoices.placeholders.address_example')"/>
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-4">
                   <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.postal_code') }}</label></div>
                   <input :value="localRecipient.person.postCode" @input="updatePerson('postCode', formatPostalCode(($event.target as HTMLInputElement).value))" class="input-base" maxlength="5" />
                </div>
            </template>

            <div class="mt-auto pt-4 flex justify-end">
               <button @click="isExpanded = !isExpanded" class="text-blue-600 text-sm font-semibold flex items-center gap-1 hover:underline">
                 {{ isExpanded ? $t('invoices.collapse') : $t('invoices.additional_fields') }}
                 <component :is="isExpanded ? ChevronUpIcon : ChevronDownIcon" class="w-4 h-4" />
               </button>
            </div>
        </div>
      </div>

      <!-- Entity Form -->
      <div v-if="localRecipient.type === RecipientType.ENTITY && localRecipient.legal" class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 items-start">
         <div class="space-y-4">
            <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.company_name') }}<span class="text-red-500 ml-0.5">*</span></label></div>
               <input :value="localRecipient.legal.name" @input="updateLegal('name', ($event.target as HTMLInputElement).value)" class="input-base" required />
            </div>
            <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.edrpou') }}<span class="text-red-500 ml-0.5">*</span></label></div>
               <input :value="localRecipient.legal.EDRPOU"
											@input="updateLegal(
												 'EDRPOU',
												 			($event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0,8)
											)"
											class="input-base" maxlength="8" required
							 />
            </div>
            <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.mfo_label') }}</label></div>
               <input :value="localRecipient.legal.MFO" @input="updateLegal('MFO', ($event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0,6))" class="input-base" maxlength="6" :placeholder="$t('invoices.placeholders.mfo_6')" />
            </div>
            <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.iban_label') }}</label></div>
               <input :value="localRecipient.legal.IBAN" @input="updateLegal('IBAN', formatIban(($event.target as HTMLInputElement).value))" class="input-base" maxlength="29" :placeholder="$t('invoices.placeholders.iban')" />
            </div>
            <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.legal_address') }}</label></div>
               <input :value="localRecipient.legal.country" @input="updateLegal('country', ($event.target as HTMLInputElement).value)" class="input-base" />
            </div>
         </div>
         <div class="space-y-4">
            <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.actual_country') }}</label></div>
               <input :value="localRecipient.legal.actualCountry" @input="updateLegal('actualCountry', ($event.target as HTMLInputElement).value)" class="input-base" />
            </div>
            <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.city') }}</label></div>
               <input :value="localRecipient.legal.city" @input="updateLegal('city', ($event.target as HTMLInputElement).value)" class="input-base" />
            </div>
            <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.street') }}</label></div>
               <input :value="localRecipient.legal.street" @input="updateLegal('street', ($event.target as HTMLInputElement).value)" class="input-base" />
            </div>
            <div class="grid grid-cols-[130px_1fr] items-center gap-4">
               <div class="text-right"><label class="text-sm font-medium text-slate-700">{{ $t('invoices.building') }}</label></div>
               <input :value="localRecipient.legal.building" @input="updateLegal('building', ($event.target as HTMLInputElement).value)" class="input-base" />
            </div>
            <div class="pl-[146px] pt-1">
               <button type="button" role="checkbox" :aria-checked="localRecipient.legal.vat" @click="updateLegal('vat', !localRecipient.legal.vat)" class="flex items-center gap-3 group">
                   <div :class="`w-6 h-6 rounded border flex items-center justify-center transition-colors ${localRecipient.legal.vat ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 group-hover:border-blue-400'}`">
                       <CheckIcon v-if="localRecipient.legal.vat" class="w-3.5 h-3.5" />
                   </div>
                   <span class="text-sm font-medium text-slate-900">{{ $t('invoices.is_vat_payer') }}</span>
               </button>
            </div>
         </div>
      </div>

      <!-- On Payment -->
      <div v-if="localRecipient.type === RecipientType.ON_PAYMENT" class="flex items-center justify-center h-40 bg-slate-50 border border-dashed border-slate-200 rounded-lg text-slate-400">
         {{ $t('invoices.on_payment') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.input-base {
	display: flex;
	width: 100%;

	border-radius: 0.375rem;
	border: 1px solid #e5e7eb;
	background-color: #ffffff;
	color: #111827;
	font-weight: 500;

	padding: 0.375rem 0.75rem;
	font-size: 0.875rem;
	line-height: 1.25rem;

	cursor: text;
}

.input-base::placeholder {
	color: #9ca3af;
	opacity: 1;
	font-weight: 400;
}

.input-base:focus-visible {
	outline: 2px solid hsl(var(--ring));
	outline-offset: 2px;
}

.input-base:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}

.input-base::file-selector-button {
	border: 0;
	background: transparent;
	font-size: 0.875rem;
	font-weight: 500;
	margin-right: 0.5rem;
}


</style>
